import type { CSSProperties, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Logger } from '../../logger/Logger';
import { VideoStatsEvent } from '../../logger/types';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { popupActions } from '../../store/slices/popup/slice';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { StreamQuality } from '../../store/slices/settings/types';
import { isCurrentBrowserIOS, isCurrentBrowserMacOSSafari } from '../../utils/browsers';
import { Icon, Icons } from '../icons';
import { Loader } from '../loader';
import { PopupType } from '../popup/types';

import { VideoControllerIOS } from './controllers/VideoControllerIOS';
import { VideoControllerV1 } from './controllers/VideoControllerV1';
import { VideoControllerV3 } from './controllers/VideoControllerV3';
import type { VideoPlayerProps } from './types';
import { useVideoRef } from './VideoRefContext';

import styles from './VideoPlayer.module.css';

export const VideoPlayer = ({
  loaderTopOffset,
  poster,
  setPosterRef,
  setVideoPlaying,
  ...rest
}: VideoPlayerProps): ReactElement => {
  const posterRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoLoader = useRef<HTMLDivElement>(null);
  const videoControllerRef = useRef<VideoControllerV3 | VideoControllerIOS | VideoControllerV1>();
  const channelLimit = videoControllerRef.current?.getChannelLimit();

  const dispatch = useCoreDispatch();

  const { setVideoRef } = useVideoRef();

  const JSESSIONID = useConfigSlice.session()?.JSESSIONID;
  const config = useConfigSlice.tableConfig();
  const countryCode = useConfigSlice.countryCode();
  const env = useConfigSlice.env();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const isGameSuspended = useGameStateSlice.getIsGameSuspended();
  const isStreamEnabled = useSettingsSlice.getIsStreamEnabled();
  const statsConfig = useConfigSlice.statsConfig();
  const streamQuality = useSettingsSlice.getStreamQuality();
  const streamQualityLimit = useSettingsSlice.getStreamQualityLimit();
  const tableId = useConfigSlice.tableId();
  const userId = useConfigSlice.userId();
  const volume = useSettingsSlice.getVolumeChannelValue('studio');

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleStats = (data: any): void => {
    let path = '';

    const url = env?.statsCollector;
    const event = data.event;
    const stats = data.data;
    switch (event) {
      case VideoStatsEvent.VideoSessionDetails:
        path = '/eventcapture/videosessionmeta';

        stats.jid = JSESSIONID;
        stats.uid = userId;
        stats.cc = countryCode;

        break;
      case VideoStatsEvent.VideoPlaybackDetails:
        path = '/eventcapture/v2/videostats';

        stats.jid = JSESSIONID;
        stats.tid = tableId;

        break;
      case VideoStatsEvent.StorePlayerDetails:
        path = '/quality/video';
        break;
      default:
        break;
    }

    if (url && stats) {
      Logger.logVideoPlayerStats(url + path, event, stats);
    }
  };

  const classicViewSwitcher = (on: boolean): void => {
    dispatch(settingsActions.setStreamClassicView(on));
  };

  useEffect(() => {
    setVideoPlaying(isStreamEnabled);
  }, [isStreamEnabled, setVideoPlaying]);

  useEffect(() => {
    setVideoRef(videoRef);
  }, [setVideoRef]);

  useEffect(() => {
    setPosterRef(posterRef);
  }, [setPosterRef]);

  useEffect(() => {
    dispatch(settingsActions.setNoVolumeControl(isCurrentBrowserIOS));
    if (isCurrentBrowserIOS) {
      dispatch(settingsActions.setAllVolumes(0));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!config || !videoRef.current || !isStreamEnabled || videoControllerRef.current || !JSESSIONID) return;
    if (isCurrentBrowserMacOSSafari) {
      videoControllerRef.current = new VideoControllerV1();
    } else if (isCurrentBrowserIOS) {
      videoControllerRef.current = new VideoControllerIOS(JSESSIONID);
    } else if (config.videoPlayerVersion === 3) {
      videoControllerRef.current = new VideoControllerV3();
    } else {
      videoControllerRef.current = new VideoControllerV1();
    }

    // Subscribe to video latency updates and store it state
    videoControllerRef.current.addLatencyListener((latency: number) => {
      dispatch(settingsActions.setStreamLatency(latency));
    });

    if (videoControllerRef.current instanceof VideoControllerV3) {
      videoControllerRef.current.videoVolume = 0;
      if (config.storeVideoQuality) {
        videoControllerRef.current.setStoreVLQ(true);
      }
      if (config.enableVideoStats) {
        videoControllerRef.current.setLogger(handleStats);
      }
    }

    if (
      videoControllerRef.current instanceof VideoControllerV3 ||
      videoControllerRef.current instanceof VideoControllerV1
    ) {
      videoControllerRef.current.setClassicViewSwitcher(classicViewSwitcher);
    }

    videoControllerRef.current.ngAfterViewInit(isCurrentBrowserIOS ? config.hlsLocation : config.videoLocation);
  }, [config, isStreamEnabled, JSESSIONID]);

  useEffect(() => {
    if (videoControllerRef.current instanceof VideoControllerV3) {
      if (config?.retrieveVideoQuality && statsConfig) {
        const arr = [statsConfig.ltpbkt0, statsConfig.ltpbkt1, statsConfig.ltpbkt2, statsConfig.ltpbkt3];
        const max = Math.max(...arr);
        const quality = arr.indexOf(max);
        if (quality >= 0) {
          videoControllerRef.current.setStatsBasedQuality(quality);
        }
      }
    }
  }, [statsConfig]);

  useEffect(() => {
    if (!isStreamEnabled || isGamePaused || isGameSuspended) {
      videoControllerRef.current?.closeSocket();
    } else {
      videoControllerRef.current?.connectSocket();
    }
  }, [isStreamEnabled, isGamePaused, isGameSuspended]);

  useEffect(() => {
    videoControllerRef.current?.onVolumeChange(volume);
  }, [volume]);

  useEffect(() => {
    videoControllerRef.current?.onChannelChanged(streamQuality);
    // TODO: remove next lines (whole if condition block) when video settings CR is approved (https://pragmaticplay.atlassian.net/browse/LCE-71231)
    if (streamQuality !== StreamQuality.Auto && streamQuality < streamQualityLimit) {
      dispatch(settingsActions.setStreamQuality(StreamQuality.Auto));
      dispatch(popupActions.pushPopup(PopupType.VideoStreamQuality));
    }
  }, [dispatch, streamQuality, streamQualityLimit]);

  useEffect(() => {
    dispatch(settingsActions.setStreamQualityLimit(channelLimit ?? StreamQuality.High));
  }, [dispatch, channelLimit]);

  return (
    <>
      <img className={styles.blur} src={poster} alt="Video poster" ref={posterRef} />
      <video
        className={`${!isStreamEnabled || isGamePaused || isGameSuspended ? styles.video_hidden : ''}`}
        data-current-player={true}
        disablePictureInPicture
        id="video" // used in controllers to get this element
        muted
        onLoadStart={(): void => setIsLoading(true)}
        onPlaying={(): void => setIsLoading(false)}
        onStalled={(): void => setIsLoading(true)}
        onWaiting={(): void => setIsLoading(true)}
        poster="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
        playsInline
        ref={videoRef}
        {...rest}
      ></video>
      {loaderTopOffset !== null && isLoading && isStreamEnabled ? (
        <div
          className={styles.videoPlayerLoader}
          style={{ '--loaderTopOffset': `${loaderTopOffset}px` } as CSSProperties}
          ref={videoLoader}
        >
          <Loader>
            <Icon icon={Icons.Video} />
          </Loader>
        </div>
      ) : null}
    </>
  );
};
